"use client";

import { useSearchParams, useRouter } from "next/navigation";
import Link from "next/link";
import { useState } from "react";
import { Map as MapIcon, ChevronDown } from "lucide-react";
import { TradePicker } from "@/app/components/TradePicker";

export function BrowseFilterBar() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const [mobileOpen, setMobileOpen] = useState(false);

  const currentRating = searchParams.get("minRating") || "";
  const currentAvailability = searchParams.get("availability") || "";
  const currentQualified = searchParams.get("qualified") || "";
  const currentSort = searchParams.get("sort") || "";
  const currentTrade = searchParams.get("trade") || "";
  const showMap = searchParams.get("map") === "true";
  // Embedded map mode is only for located searches. Without a location,
  // send people to the live UK activity map instead.
  const hasSearch = !!(
    searchParams.get("postcode") || searchParams.get("name")
  );

  function updateParam(key: string, value: string) {
    const params = new URLSearchParams(searchParams.toString());
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    router.push(`/browse?${params.toString()}`);
  }

  const hasActiveFilters = !!(
    currentRating ||
    currentAvailability ||
    currentQualified
  );

  return (
    <section className="bg-warm-bg sticky top-0 z-40 border-b border-stone/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4">
        {/* Mobile: filter toggle + map/sort */}
        <div className="flex items-center justify-between md:hidden">
          <button
            type="button"
            onClick={() => setMobileOpen((s) => !s)}
            className="flex items-center gap-2 text-sm font-bold text-text-muted hover:text-forest transition-colors bg-transparent border-none cursor-pointer py-3"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              />
            </svg>
            Filters
            {hasActiveFilters && (
              <span className="w-2 h-2 rounded-full bg-forest" />
            )}
          </button>
          <div className="flex items-center gap-4">
            {hasSearch ? (
              <>
                <button
                  type="button"
                  onClick={() => updateParam("map", showMap ? "" : "true")}
                  className="flex items-center gap-2 text-sage font-bold text-sm hover:opacity-70 transition-opacity bg-transparent border-none cursor-pointer py-3"
                >
                  <MapIcon className="w-4 h-4" />
                  {showMap ? "Hide Map" : "Show Map"}
                </button>
                <div className="h-4 w-px bg-stone/50" />
              </>
            ) : (
              <>
                <Link
                  href="/map"
                  className="flex items-center gap-2 text-sage font-bold text-sm hover:opacity-70 transition-opacity py-3 no-underline"
                >
                  <MapIcon className="w-4 h-4" />
                  View Live Map
                </Link>
                <div className="h-4 w-px bg-stone/50" />
              </>
            )}
            <SortSelect
              value={currentSort}
              onChange={(v) => updateParam("sort", v)}
            />
          </div>
        </div>
        <div className="mt-3 md:hidden">
          <TradeFilter value={currentTrade} onChange={(v) => updateParam("trade", v)} />
        </div>

        {/* Mobile expanded / Desktop always visible */}
        <div
          className={`${mobileOpen ? "flex" : "hidden"} md:flex flex-wrap items-center justify-between gap-6 ${mobileOpen ? "mt-3 pt-3 border-t border-stone/20" : ""}`}
        >
          <div className="flex flex-wrap items-center gap-4">
            <div className="hidden md:block">
              <TradeFilter value={currentTrade} onChange={(v) => updateParam("trade", v)} />
            </div>

            {/* Rating pills */}
            <div className="flex items-center bg-stone/40 rounded-full p-1">
              {[
                { label: "Any", value: "" },
                { label: "4+", value: "4" },
                { label: "4.5+", value: "4.5" },
              ].map((opt) => (
                <button
                  key={opt.label}
                  type="button"
                  onClick={() => updateParam("minRating", opt.value)}
                  className={`px-5 py-2.5 rounded-full text-xs font-bold transition-all cursor-pointer border-none ${
                    currentRating === opt.value
                      ? "bg-white text-forest shadow-sm"
                      : "bg-transparent text-text-muted hover:bg-white/50"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {/* Availability dropdown */}
            <div className="relative">
              <select
                value={currentAvailability}
                onChange={(e) =>
                  updateParam("availability", e.target.value)
                }
                aria-label="Availability"
                className="bg-stone/40 border-none rounded-full pl-6 pr-10 py-2.5 text-xs font-bold text-text-muted focus:ring-0 appearance-none cursor-pointer outline-none"
              >
                <option value="">Any availability</option>
                <option value="now">Available now</option>
                <option value="within_2_weeks">Within 2 weeks</option>
                <option value="within_4_weeks">Within 4 weeks</option>
              </select>
              <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-3 h-3 text-text-muted pointer-events-none" />
            </div>

            {/* Qualified toggle */}
            <label className="flex items-center gap-2 cursor-pointer bg-stone/40 rounded-full px-4 py-2.5 group">
              <div className="relative flex items-center">
                <input
                  type="checkbox"
                  checked={currentQualified === "1"}
                  onChange={(e) =>
                    updateParam("qualified", e.target.checked ? "1" : "")
                  }
                  className="peer sr-only"
                />
                <div className="w-8 h-4 bg-text-muted/30 rounded-full peer-checked:bg-forest transition-colors" />
                <div className="absolute left-0.5 w-3 h-3 bg-white rounded-full transition-transform peer-checked:translate-x-4" />
              </div>
              <span className="text-xs font-bold text-text-muted">
                Qualified only
              </span>
            </label>
          </div>

          {/* Right side: Show Map + divider + Sort */}
          <div className="hidden md:flex items-center gap-4 ml-auto">
            {hasSearch ? (
              <>
                <button
                  type="button"
                  onClick={() => updateParam("map", showMap ? "" : "true")}
                  className="flex items-center gap-2 text-sage font-bold text-sm hover:opacity-70 transition-opacity bg-transparent border-none cursor-pointer py-3"
                >
                  <MapIcon className="w-4 h-4" />
                  {showMap ? "Hide Map" : "Show Map"}
                </button>
                <div className="h-4 w-px bg-stone/50" />
              </>
            ) : (
              <>
                <Link
                  href="/map"
                  className="flex items-center gap-2 text-sage font-bold text-sm hover:opacity-70 transition-opacity py-3 no-underline"
                >
                  <MapIcon className="w-4 h-4" />
                  View Live Map
                </Link>
                <div className="h-4 w-px bg-stone/50" />
              </>
            )}
            <SortSelect
              value={currentSort}
              onChange={(v) => updateParam("sort", v)}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function SortSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Sort results"
        className="bg-transparent border-none text-sm font-bold text-text-muted focus:ring-0 cursor-pointer pr-6 appearance-none outline-none"
      >
        <option value="">Sort: Relevance</option>
        <option value="distance">Sort: Distance</option>
        <option value="rating">Sort: Rating</option>
        <option value="google">Sort: Google reviews</option>
      </select>
      <ChevronDown className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 text-text-muted pointer-events-none" />
    </div>
  );
}

function TradeFilter({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="relative w-full sm:w-[230px]">
      <TradePicker
        value={value}
        onChange={onChange}
        placeholder="Browse by trade"
        ariaLabel="Browse by trade"
        className="w-full min-h-10 px-5 py-2.5 bg-stone/40 rounded-full text-xs font-bold text-text-muted"
      />
    </div>
  );
}
